/**
 * The instance answers **every** authentication problem with `403` and a JSON body, and never with
 * `401`. Measured against `/api/v1/auth/*`:
 *
 * | body `error`                              | meaning                                  |
 * |-------------------------------------------|------------------------------------------|
 * | `Request must be authenticated`           | no `Authorization` header at all         |
 * | `Token is expired, please try again`      | the token's `expire` claim is past        |
 * | `Invalid signature`                       | tampered, or minted by another instance  |
 * | `Invalid scope`                           | valid token, endpoint not granted        |
 * | `Unexpected char 'g' at line 1, column 1` | the token was not JSON — a parser error  |
 *
 * That last row is the instance leaking its own JSON parser's message into an API response; it is
 * matched on shape rather than on wording, because the character and column vary with the input.
 *
 * Why this matters enough to have its own module: **`403` is overloaded across two subsystems.**
 * `classifyHttpError` maps `403` to `urls-expired`, which is right for a signed media URL and
 * wrong for every row above — it would send the player off to reload a manifest because the user
 * is signed out. Nothing but the body can tell the two apart.
 */
export type AuthFailureKind =
  | 'not-authenticated'
  | 'token-expired'
  | 'invalid-signature'
  | 'invalid-scope'
  | 'malformed-token'
  /** A `403` from an authenticated endpoint whose body we do not recognise. */
  | 'unknown';

/** Crystal's `JSON::ParseException`, as it reaches the client. */
const PARSER_ERROR_RE = /^(unexpected|expected|unterminated|invalid)\b.*\bat line \d+, column \d+/i;

function errorMessageOf(body: string): string {
  const trimmed = body.trim();
  if (trimmed === '') return '';
  // Deliberately a regex rather than `JSON.parse` (R4: core never parses JSON text), and it also
  // survives a body that is not JSON at all.
  const match = /"error"\s*:\s*"((?:[^"\\]|\\.)*)"/.exec(trimmed);
  if (match?.[1] === undefined) return trimmed;
  return match[1].replace(/\\"/g, '"').replace(/\\\\/g, '\\');
}

/**
 * `null` means "this is not an authentication failure" — either the status is not 403, or the body
 * is something else entirely, and the caller should fall back to the ordinary HTTP classifier.
 */
export function classifyAuthFailure(status: number, body: string): AuthFailureKind | null {
  if (status !== 403 && status !== 401) return null;
  // A 401 never happens on this instance, but an instance that behaves *correctly* would send one,
  // and treating it as anything other than an auth failure would be perverse.
  const message = errorMessageOf(body);
  if (status === 401 && message === '') return 'not-authenticated';
  if (message === '') return null;

  const lower = message.toLowerCase();
  if (lower.includes('must be authenticated')) return 'not-authenticated';
  if (lower.includes('token is expired') || lower.includes('token expired')) return 'token-expired';
  if (lower.includes('invalid signature')) return 'invalid-signature';
  if (lower.includes('invalid scope')) return 'invalid-scope';
  if (PARSER_ERROR_RE.test(message)) return 'malformed-token';
  return status === 401 ? 'unknown' : null;
}

/**
 * What the app should actually do, which is not the same for all five. Getting this wrong in the
 * obvious direction — "any auth 403 means re-authorize" — produces a consent-screen loop on
 * `invalid-scope`, because re-running the *same* grant request mints the *same* insufficient token.
 */
export type AuthRecovery =
  /** Discard the stored token and show the sign-in affordance. The credential is unusable. */
  | 'sign-in'
  /** Re-run the grant flow unchanged; the user is still who they were, the token just aged out. */
  | 'reauthorize'
  /** Re-run the grant flow **asking for more** — repeating the current request cannot succeed. */
  | 'widen-scopes';

export function decideAuthRecovery(kind: AuthFailureKind): AuthRecovery {
  switch (kind) {
    case 'token-expired':
      return 'reauthorize';
    case 'invalid-scope':
      return 'widen-scopes';
    case 'not-authenticated':
    case 'invalid-signature':
    case 'malformed-token':
    case 'unknown':
      return 'sign-in';
  }
}
